// The component that keeps talking to the user while the experience build phase
// is in progress.

import soulIntroUrl from 'assets/videos/soul-intro-short.wav';

import {
  type FunctionComponent,
  useEffect,
  useEffectEvent,
  useState,
} from 'react';

import { SEC_MS } from '@dr.pogodin/js-utils';

import TypewriterText from 'components/TypewriterText';
import { getAudioClientService } from 'services';

// import { speech } from 'services/ai';

import { useGlobalState } from 'utils';

import './style.scss';

type PropsT = {
  experienceIsReady: boolean;

  /** Fired once, the moment this switches from the "Building" to the
   *  "Your Experience is Ready" text. */
  onReady?: () => void;
};

// const FINAL_MESSAGE = 'Now your experience is ready. You may start it by pressing the “Start Your Session” button, or you can edit it manually according to your preferences.';

type GroundingLineT = {
  readonly atMs: number;
  readonly text: string;
};

const GROUNDING_LINES: readonly GroundingLineT[] = [
  { atMs: 0, text: 'Take a moment to check in with yourself.' },
  { atMs: 4000, text: 'Notice how you’re arriving in this moment.' },
  { atMs: 7000, text: 'How you feel physically,' },
  { atMs: 8000, text: 'how you feel mentally,' },
  { atMs: 10000, text: 'how present or distracted your mind might be right now.' },
  { atMs: 16000, text: 'There’s no right or wrong way to arrive.' },
  { atMs: 18000, text: 'The fact that you’re taking the time to notice' },
  { atMs: 20000, text: 'is more than enough.' },
  { atMs: 23000, text: 'If you have a hard time knowing or' },
  { atMs: 24000, text: 'describing how you are in this moment,' },
  { atMs: 26000, text: 'that is okay too.' },
  { atMs: 30000, text: 'Let us take a breath together' },
  { atMs: 32000, text: 'before you begin your experience.' },
  { atMs: 35000, text: 'Use this breath as a transition from your day' },
  { atMs: 37000, text: 'into the sanctuary you created for yourself.' },
  { atMs: 40000, text: 'Breathe through your nose or your mouth,' },
  { atMs: 43000, text: 'whatever feels most natural for you.' },
  { atMs: 46000, text: 'Breathe in, and breathe out.' },
  { atMs: 52000, text: 'Enjoy your practice.' },
];

const VISIBLE_LINES = 3;

// const WAIT_MORE_MESSAGE = 'Your experience is ready in a moment.';

// NOTE: This is intended for client-side use only, and we are not supposed
// to play more than a single audio message a time, thus it is fine to have it
// just a (module) global variable.
let source: AudioBufferSourceNode | undefined;

const GROUNDING_WATCHDOG_MS = 12 * SEC_MS;

export const Talker: FunctionComponent<PropsT> = ({
  experienceIsReady,
  onReady,
}) => {
  const [user] = useGlobalState('user');
  if (!user) throw Error('Unauthenticated');

  const [lineIndex, setLineIndex] = useState(0);

  const [done, setDone] = useState(false);
  const [groundingDone, setGroundingDone] = useState(false);

  const onReadyEvent = useEffectEvent(() => {
    onReady?.();
  });

  useEffect(() => {
    if (done) onReadyEvent();
  }, [done]);

  // If the grounding (below) was completed before the experience was ready,
  // this effect is entered once (protected by the "done" state) and plays
  // the final "experience" is ready message.
  const playExperienceIsReady = !done && experienceIsReady && groundingDone;
  useEffect(() => {
    if (playExperienceIsReady) {
      queueMicrotask(() => {
        setDone(true);

        // setText((prev) => [...prev, { text: `\n\n${FINAL_MESSAGE}` }]);
        // void (async () => {
        //   const buffer = await speech(FINAL_MESSAGE, {
        //     voice: user.voice ?? DEFAULT_VOICE,
        //   });
        //
        //   const audio = await getAudioClientService();
        //   const context = audio.getAudioContext();
        //   await context.resume();
        //
        //   source?.stop();
        //   source = new AudioBufferSourceNode(context, { buffer });
        //   source.connect(context.destination);
        //   source.start();
        // })();
      });
    }
  }, [playExperienceIsReady]);

  // Once the main grounding narration (the useEffect() below) is completed,
  // this either tells the experience is ready, or that it will be ready in
  // a moment.
  const onGroundingDone = useEffectEvent(() => {
    if (experienceIsReady) setDone(true);

    // const message = experienceIsReady ? FINAL_MESSAGE : WAIT_MORE_MESSAGE;
    // void (async () => {
    //   setText([...text, { text: `\n\n${message}` }]);
    //   const buffer = await speech(message, {
    //     voice: user.voice ?? DEFAULT_VOICE,
    //   });
    //
    //   const audio = await getAudioClientService();
    //   const context = audio.getAudioContext();
    //   await context.resume();
    //
    //   source?.stop();
    //   source = new AudioBufferSourceNode(context, { buffer });
    //   source.connect(context.destination);
    //   source.start();
    // })();

    setGroundingDone(true);
  });

  // This useEffect() narrates the main grounding text, and once completed
  // it either triggers the narration that the experience is ready, or that
  // it will be ready in a moment.
  useEffect(() => {
    // This is a quick workaround, to ensure that on "ended" message is not
    // played when the component is unmounted, thus interrupting the main
    // speech block.
    let mounted = true;

    let signaled = false;
    const signalGroundingDone = () => {
      if (mounted && !signaled) {
        signaled = true;
        onGroundingDone();
      }
    };

    let watchdog = setTimeout(signalGroundingDone, GROUNDING_WATCHDOG_MS);

    const lineTimers: NodeJS.Timeout[] = [];

    void (async () => {
      try {
        const audio = await getAudioClientService();
        const context = audio.getAudioContext();
        await context.resume();

        const res = await fetch(soulIntroUrl);
        const arrayBuffer = await res.arrayBuffer();
        const buffer = await context.decodeAudioData(arrayBuffer);

        source?.stop();
        source = new AudioBufferSourceNode(context, { buffer });
        source.connect(context.destination);
        source.addEventListener('ended', signalGroundingDone);
        source.start();

        for (let i = 1; i < GROUNDING_LINES.length; ++i) {
          const line = GROUNDING_LINES[i];
          if (!line) throw Error('Missing line');
          lineTimers.push(
            setTimeout(() => {
              setLineIndex(i);
            }, line.atMs),
          );
        }

        clearTimeout(watchdog);
        watchdog = setTimeout(
          signalGroundingDone,
          buffer.duration * SEC_MS + SEC_MS,
        );
      } catch {
        signalGroundingDone();
      }
    })();

    // TODO: This is actually not 100% correct - we should synchronise it with
    // the playback initialization above, so that if the effect is unmounted
    // any inflight initialization is aborted as well (currently it is not).
    // In practice, as this component is used, I guess the possibility to
    // hit this problem is tiny now.
    return () => {
      mounted = false;
      clearTimeout(watchdog);
      lineTimers.forEach(clearTimeout);
      source?.stop();
    };
  }, []);

  const windowStart = Math.max(0, lineIndex - (VISIBLE_LINES - 1));

  return (
    <div styleName="container">
      <div styleName="header">
        {done
          ? <>Your Experience <br /> is Ready</>
          : <>Building <br /> Your Experience</>}
      </div>
      {done ? null : (
        <div styleName="text">
          {GROUNDING_LINES.slice(windowStart, lineIndex + 1).map((line, i) => {
            const index = windowStart + i;
            return index === lineIndex
              ? <TypewriterText key={index} pre>{line.text}</TypewriterText>
              : <pre key={index}>{line.text}</pre>;
          })}
        </div>
      )}
    </div>
  );
};
